/**
 * React Context for Computation Graph
 *
 * Provides graph state management and reactive updates
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import {
  Graph,
  GraphDefinition,
  NodeId,
  Node,
  DerivationTree,
  isAssumptionNode,
} from '../types';
import {
  createGraph,
  compute,
  getValue,
  getNode,
  getDeps,
  getDependents,
  getDerivation,
  isOverridden,
  formatValue,
} from '../runtime';

/**
 * Context value interface
 */
export interface GraphContextValue {
  // The graph instance
  graph: Graph;
  // All computed values
  values: Map<NodeId, number>;
  // Current overrides
  overrides: Map<NodeId, number>;
  // Set an override for an assumption
  setOverride: (nodeId: NodeId, value: number) => void;
  // Clear an override
  clearOverride: (nodeId: NodeId) => void;
  // Clear all overrides
  clearAllOverrides: () => void;
  // Currently inspected node (for sidebar)
  inspectedNodeId: NodeId | null;
  // Set the inspected node
  setInspectedNodeId: (nodeId: NodeId | null) => void;
  // Get a specific value
  getValue: (nodeId: NodeId) => number | undefined;
  // Get a specific node definition
  getNode: (nodeId: NodeId) => Node | undefined;
  // Get dependencies
  getDeps: (nodeId: NodeId) => NodeId[];
  // Get dependents
  getDependents: (nodeId: NodeId) => NodeId[];
  // Get derivation tree
  getDerivation: (nodeId: NodeId) => DerivationTree | undefined;
  // Check if overridden
  isOverridden: (nodeId: NodeId) => boolean;
  // Format a value
  formatValue: (value: number, unit: string, options?: { decimals?: number; compact?: boolean }) => string;
}

const GraphContext = createContext<GraphContextValue | null>(null);

/**
 * Storage key for persisting overrides
 */
function getStorageKey(persistKey: string): string {
  return `computation-graph-overrides-${persistKey}`;
}

/**
 * Load overrides from localStorage
 */
function loadOverrides(persistKey: string): Map<NodeId, number> {
  if (typeof window === 'undefined') return new Map();

  try {
    const stored = localStorage.getItem(getStorageKey(persistKey));
    if (stored) {
      const parsed = JSON.parse(stored);
      return new Map(Object.entries(parsed));
    }
  } catch {
    // Ignore errors
  }
  return new Map();
}

/**
 * Save overrides to localStorage
 */
function saveOverrides(persistKey: string, overrides: Map<NodeId, number>): void {
  if (typeof window === 'undefined') return;

  try {
    const obj = Object.fromEntries(overrides);
    localStorage.setItem(getStorageKey(persistKey), JSON.stringify(obj));
  } catch {
    // Ignore errors
  }
}

/**
 * Props for GraphProvider
 */
export interface GraphProviderProps {
  definition: GraphDefinition;
  children: ReactNode;
  // Key for persisting overrides to localStorage
  persistKey?: string;
}

/**
 * Graph Provider Component
 */
export function GraphProvider({
  definition,
  children,
  persistKey,
}: GraphProviderProps): React.ReactElement {
  // Initialize graph with persisted overrides
  const [graph] = useState<Graph>(() => {
    const g = createGraph(definition);
    if (persistKey) {
      const savedOverrides = loadOverrides(persistKey);
      for (const [id, value] of savedOverrides) {
        if (g.definition.nodes[id] && isAssumptionNode(g.definition.nodes[id])) {
          g.overrides.set(id, value);
        }
      }
      compute(g);
    }
    return g;
  });

  // Track values for reactivity
  const [values, setValues] = useState<Map<NodeId, number>>(
    () => new Map(graph.values)
  );

  // Track overrides for reactivity
  const [overrides, setOverrides] = useState<Map<NodeId, number>>(
    () => new Map(graph.overrides)
  );

  // Currently inspected node
  const [inspectedNodeId, setInspectedNodeId] = useState<NodeId | null>(null);

  // Set an override
  const handleSetOverride = useCallback(
    (nodeId: NodeId, value: number) => {
      graph.overrides.set(nodeId, value);
      compute(graph);
      setValues(new Map(graph.values));
      setOverrides(new Map(graph.overrides));

      if (persistKey) {
        saveOverrides(persistKey, graph.overrides);
      }
    },
    [graph, persistKey]
  );

  // Clear an override
  const handleClearOverride = useCallback(
    (nodeId: NodeId) => {
      graph.overrides.delete(nodeId);
      compute(graph);
      setValues(new Map(graph.values));
      setOverrides(new Map(graph.overrides));

      if (persistKey) {
        saveOverrides(persistKey, graph.overrides);
      }
    },
    [graph, persistKey]
  );

  // Clear all overrides
  const handleClearAllOverrides = useCallback(() => {
    graph.overrides.clear();
    compute(graph);
    setValues(new Map(graph.values));
    setOverrides(new Map(graph.overrides));

    if (persistKey) {
      saveOverrides(persistKey, graph.overrides);
    }
  }, [graph, persistKey]);

  // Memoized context value
  const contextValue = useMemo<GraphContextValue>(
    () => ({
      graph,
      values,
      overrides,
      setOverride: handleSetOverride,
      clearOverride: handleClearOverride,
      clearAllOverrides: handleClearAllOverrides,
      inspectedNodeId,
      setInspectedNodeId,
      getValue: (nodeId) => getValue(graph, nodeId),
      getNode: (nodeId) => getNode(graph, nodeId),
      getDeps: (nodeId) => getDeps(graph, nodeId),
      getDependents: (nodeId) => getDependents(graph, nodeId),
      getDerivation: (nodeId) => getDerivation(graph, nodeId),
      isOverridden: (nodeId) => isOverridden(graph, nodeId),
      formatValue,
    }),
    [
      graph,
      values,
      overrides,
      handleSetOverride,
      handleClearOverride,
      handleClearAllOverrides,
      inspectedNodeId,
    ]
  );

  return (
    <GraphContext.Provider value={contextValue}>
      {children}
    </GraphContext.Provider>
  );
}

/**
 * Hook to access the graph context
 */
export function useGraph(): GraphContextValue {
  const context = useContext(GraphContext);
  if (!context) {
    throw new Error('useGraph must be used within a GraphProvider');
  }
  return context;
}

export { GraphContext };
